import {
  faArrowsFromLine, faArrowsToLine,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useBoundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import {
  closeMapTool, openMapTool,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { useFrontendStateLeftSidebarOpenedMapToolSelector } from '~/store/frontendState/leftSidebar/leftSidebar.selectors';
import { useMapSettingsExportImageSettingsSelector } from '~/store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '~/store/selectors/useMapInfoSelectors';
import { type SidebarAppProps } from '../sidebarAppComponentProps.type';
import { MapToolsComponent } from './mapTools.component';
import { standaloneMapTools } from './mapTools/mapTools.helpers';
import { useToolCollapseState } from './mapTools/useToolCollapseState';

export const MapToolsContainer: FC<SidebarAppProps> = props => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const selectedTool = useFrontendStateLeftSidebarOpenedMapToolSelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isMobileScreen = useIsMobileScreenSelector();
  const activeBoundaryToolBoundaryTerritoryGroups = useBoundaryToolActiveBTGsSelector();
  const isImageExportMode = !!useMapSettingsExportImageSettingsSelector()?.mode;
  const { isToolCollapsibleDisabled, isToolCollapsible, isToolCollapsed, setToolCollapse } = useToolCollapseState();

  const isToolSelectEnabled = !isMapPresentational && !isMobileScreen
    && notNullsy(selectedTool) && !standaloneMapTools.includes(selectedTool);

  const isBoundaryToolDisabled = isEditModeDisabled && activeBoundaryToolBoundaryTerritoryGroups.length < 1;

  const additionalToolboxAction = useMemo(() => {
    if (!isToolSelectEnabled || !isToolCollapsible) {
      return undefined;
    }
    else {
      return {
        icon: isToolCollapsed ? faArrowsFromLine : faArrowsToLine,
        onClick: () => {
          setToolCollapse(!isToolCollapsed);
        },
        isDisabled: isToolCollapsibleDisabled,
        isActive: isToolCollapsed,
        tooltipText: isToolCollapsed ? t('Expand Tool') : t('Collapse Tool'),
      };
    }
  }, [isToolSelectEnabled, isToolCollapsible, isToolCollapsed, isToolCollapsibleDisabled, t, setToolCollapse]);

  return (
    <MapToolsComponent
      additionalToolboxAction={additionalToolboxAction}
      closeTool={() => dispatch(closeMapTool())}
      isBoundaryToolDisabled={isBoundaryToolDisabled}
      isEditModeDisabled={isEditModeDisabled}
      isToolSelectEnabled={isToolSelectEnabled}
      onClose={isImageExportMode ? undefined : props.onClose}
      openTool={(tool) => dispatch(openMapTool(tool))}
      selectedTool={selectedTool}
    />
  );
};
